// src/layouts/StaticPageLayout.tsx

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import BackButton from '../components/common/BackButton';
import { Button } from '../components/common/Button';

interface StaticPageLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  metaTitle?: string;
  metaDesc?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  centered?: boolean;
  showCta?: boolean;
  ctaText?: string;
  ctaPath?: string;
  onCtaClick?: () => void;
  canonicalUrl?: string;
  /** Soft-404 / missing pages: do not ask Google to index the SPA shell. */
  noIndex?: boolean;
  schemaData?: object;
}

const StaticPageLayout: React.FC<StaticPageLayoutProps> = ({
  children,
  title,
  subtitle,
  metaTitle,
  metaDesc,
  showBackButton = false,
  onBack,
  centered = true,
  showCta = false,
  ctaText,
  ctaPath,
  onCtaClick,
  canonicalUrl,
  noIndex = false,
  schemaData
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUrl = canonicalUrl || window.location.href.split('?')[0];
  /** Prefer dedicated SEO meta; never fall back OG/twitter to UI H1 (DynamicPageMeta owns defaults). */
  const ogTitle = metaTitle || undefined;
  const ogDesc = metaDesc || undefined;

  const handleCtaClick = () => {
    onCtaClick?.();
    if (ctaPath) {
      navigate(ctaPath);
    }
  };

  return (
    <>
      <Helmet>
        {metaTitle && <title>{metaTitle}</title>}
        {metaDesc && <meta name="description" content={metaDesc} />}
        {noIndex ? (
          <meta name="robots" content="noindex, follow" />
        ) : (
          <link rel="canonical" href={currentUrl} />
        )}
        {ogTitle && <meta property="og:title" content={ogTitle} />}
        {ogDesc && <meta property="og:description" content={ogDesc} />}
        <meta property="og:type" content="website" />
        {!noIndex && <meta property="og:url" content={currentUrl} />}
        <meta property="og:site_name" content="Architect-IQ" />
        <meta property="og:image" content="https://www.architectiq.io/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        {ogTitle && <meta name="twitter:title" content={ogTitle} />}
        {ogDesc && <meta name="twitter:description" content={ogDesc} />}
        <meta name="twitter:image" content="https://www.architectiq.io/og-image.png" />
        {schemaData && (
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        )}
      </Helmet>

      <div className={`w-full min-w-0 flex-grow relative overflow-x-clip px-3 py-4 sm:p-8 flex flex-col ${centered ? 'items-center justify-center' : 'items-stretch sm:items-center justify-start pt-16 sm:pt-20'}`}>

        {showBackButton && (
          <BackButton
            onClick={onBack}
            label={t('common.back')}
            className="absolute top-4 left-4 sm:top-8 sm:left-8 z-20"
          />
        )}

        {(title || subtitle) && (
          <div className="relative z-10 text-center w-full max-w-4xl mx-auto mb-6 sm:mb-12 pt-4 sm:pt-8 md:pt-0 px-1">
            {title && (
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-2 sm:mb-4 text-white font-poppins leading-tight text-balance">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-gray-300 text-sm sm:text-lg md:text-xl leading-relaxed max-w-3xl mx-auto text-pretty">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className="relative z-10 w-full min-w-0 max-w-7xl mx-auto flex flex-col items-stretch">
          {children}

          {showCta && (
            <div className="mt-10 sm:mt-16 flex justify-center w-full px-2 sm:px-0 pb-8 sm:pb-12">
              <Button
                onClick={handleCtaClick}
                size="lg"
                variant="primary"
                icon={<ArrowRight />}
                className="w-full max-w-md sm:w-auto sm:max-w-none shadow-lg shadow-green-500/20 hover:shadow-green-500/40 sm:hover:-translate-y-1 transition-transform"
              >
                {ctaText || t('common.start_now')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StaticPageLayout;